// The KnowledgeClient + GraphStore seams (data-model.md:49; D40/D43; ADR-0019).
//
// Every method is tenant-scoped by CONSTRUCTION: `accountId` (and `appId` where an app is the
// subject) are REQUIRED positional arguments, mirroring RetrievalClient (retrieval.ts) - a
// knowledge query cannot be issued without a tenant scope. One FalkorDB graph per tenant and
// per-tenant Qdrant collections (knowledge-architecture.md:47-48,90); the per-app chat is scoped
// to ONE app and cannot reach another app's knowledge (D43). This file holds the seam interfaces
// plus the customer-facing knowledge payloads ("your app, explained" and "ask your crew",
// knowledge-architecture.md:78); the HTTP wire envelope is in ../http/knowledge.ts.
import { z } from "zod";
import { KnowledgeNodeId } from "../ids.js";
import { NonEmptyString } from "../model/common.js";
import { RiskLevel } from "../model/enums.js";
// --- Ask-your-crew language (D53) ---
/**
 * The eight LTR launch languages ask-your-crew answers in (D53, decisions-adr.md:141): English,
 * Dutch, French, German, Spanish, Italian, Portuguese, Polish. Distinct from `Locale` (en|nl,
 * the UI locale): ask-crew auto-detects the founder's language and replies in it. The model can
 * converse beyond this set (D53), but the typed surface pins the eight launch languages.
 */
export const DetectedLanguage = z.enum(["en", "nl", "fr", "de", "es", "it", "pt", "pl"]);
// --- Customer-facing knowledge payloads (knowledge-architecture.md:78) ---
/**
 * A grounding pointer for an ask-crew fact (knowledge-architecture.md:63: "every retrieved fact
 * is cited (path and line, or node id)"). `ref` is the human-readable pointer (a `path:line` or
 * a source ref); `nodeId` links to the graph node when the fact is a node. No PII - citations and
 * the risk map are redacted from logs (knowledge-architecture.md:94).
 */
export const Citation = z.object({
    ref: NonEmptyString,
    nodeId: KnowledgeNodeId.nullable(),
    label: NonEmptyString.nullable(),
});
/** One feature or workflow the app implements (knowledge-architecture.md:78). Links to its graph node. */
export const KnowledgeFeature = z.object({
    nodeId: KnowledgeNodeId.nullable(),
    label: NonEmptyString,
    summary: NonEmptyString,
});
/**
 * One data-map entry: WHERE data lives and whether personal data sits there
 * (knowledge-architecture.md:78). `holdsPii` is a flag, never the personal data itself - this
 * surface carries no PII.
 */
export const DataMapEntry = z.object({
    nodeId: KnowledgeNodeId.nullable(),
    entity: NonEmptyString,
    holdsPii: z.boolean(),
    summary: NonEmptyString,
});
/** One dependency or integration (knowledge-architecture.md:78). */
export const DependencyEntry = z.object({
    nodeId: KnowledgeNodeId.nullable(),
    name: NonEmptyString,
    summary: NonEmptyString,
});
/**
 * A ranked risk (knowledge-architecture.md:78,94; ADR-0019 decision 2). RANKING IS ARRAY ORDER -
 * there is NO ordinal `rank` field; `KnowledgeDoc.rankedRisks` is ordered highest-first. `severity`
 * uses the centralized `RiskLevel` set (model/enums.ts). The risk map is sensitive (treated like a
 * security finding) but PII-free by construction.
 */
export const RankedRisk = z.object({
    nodeId: KnowledgeNodeId.nullable(),
    label: NonEmptyString,
    severity: RiskLevel,
    summary: NonEmptyString,
});
/**
 * "Your app, explained" (knowledge-architecture.md:78; slice-plan.md:55). A plain-language read
 * of the app graph. The arrays are BOUNDED summaries derived by the RAG pipeline (community
 * summaries keep them small, knowledge-architecture.md:70), not raw node dumps, so this composed
 * per-app resource is not a growing list endpoint. Fix history is a later addition; this pins the
 * Slice-3 acceptance set (features, data map, dependencies, ranked risks).
 */
export const KnowledgeDoc = z.object({
    features: z.array(KnowledgeFeature),
    dataMap: z.array(DataMapEntry),
    dependencies: z.array(DependencyEntry),
    rankedRisks: z.array(RankedRisk),
});
/**
 * An ask-your-crew question, scoped to one app by the route (D43). `preferredLanguage` is the
 * founder's OPTIONAL correction affordance after a drifted detection (ADR-0019 decision 4);
 * auto-detect stays the default (D53). The question is bounded at 4000 characters at the validation
 * wall (ADR-0019 decision 2 - an input bound, tunable by ADR), because it is untrusted input and
 * retrieved content is data, never instructions (knowledge-architecture.md:93).
 */
export const AskCrewQuestion = z.object({
    question: z.string().trim().min(1).max(4000),
    preferredLanguage: DetectedLanguage.nullable().optional(),
});
/**
 * The answer (ADR-0019 decision 4). `kind` is the wire discriminator: `answer` (a grounded reply),
 * `abstain` (not enough to answer), or `feature_request` (a recognized build ask that can open the
 * feature lane, slice-plan.md:57). Cited-or-abstain is enforced at the schema (D40 - every retrieved
 * fact is cited): `kind = "answer"` REQUIRES a non-empty `knows`, so an uncited confident answer is
 * unrepresentable; `abstain`/`feature_request` may carry an empty `knows`. `knows` cites
 * nodes/passages; `assumes` is plain, uncited statements (slice-plan.md:56; context-discipline).
 */
export const AskCrewAnswer = z
    .object({
    kind: z.enum(["answer", "abstain", "feature_request"]),
    answer: NonEmptyString,
    language: DetectedLanguage,
    knows: z.array(Citation),
    assumes: z.array(NonEmptyString),
})
    .refine((value) => value.kind !== "answer" || value.knows.length >= 1, {
    message: "an answer must cite at least one source (cited-or-abstain, D40)",
    path: ["knows"],
});
//# sourceMappingURL=knowledge.js.map